import { useEffect } from 'react';
import { Bell, X } from 'lucide-react';
import { cn } from '@/utils';

interface NewNotificationBannerProps {
  visible: boolean;
  title: string;
  onOpen: () => void;
  onDismiss: () => void;
}

export function NewNotificationBanner({
  visible,
  title,
  onOpen,
  onDismiss,
}: NewNotificationBannerProps) {
  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(onDismiss, 5000);
    return () => clearTimeout(timer);
  }, [visible, onDismiss]);

  return (
    <div
      className={cn(
        'fixed top-4 right-4 z-[60] flex items-center gap-3 px-4 py-3 rounded-xl',
        'bg-white border border-[#EAAD07]/40 shadow-[0_4px_24px_rgba(7,44,80,0.2)]',
        'transition-all duration-500 cursor-pointer select-none',
        visible
          ? 'translate-y-0 opacity-100 pointer-events-auto'
          : '-translate-y-6 opacity-0 pointer-events-none'
      )}
      onClick={onOpen}
      role="alert"
    >
      {/* Animated bell */}
      <div className="flex-shrink-0 h-9 w-9 rounded-full bg-[#EAAD07]/15 flex items-center justify-center">
        <Bell className="h-5 w-5 text-[#EAAD07] animate-bell" />
      </div>

      <div className="flex flex-col min-w-0">
        <span className="text-[#072C50] font-semibold text-sm leading-tight">
          {title || 'New Notification'}
        </span>
        <span className="text-[#072C50]/60 text-xs mt-0.5">Click to view</span>
      </div>

      {/* Dismiss */}
      <button
        className="ml-2 flex-shrink-0 text-[#072C50]/40 hover:text-[#072C50] transition-colors"
        onClick={(e) => {
          e.stopPropagation();
          onDismiss();
        }}
      >
        <X className="h-4 w-4" />
      </button>

      {/* Progress bar */}
      {visible && (
        <span
          className="absolute bottom-0 left-0 h-[2px] bg-[#EAAD07] rounded-b-xl animate-[shrink_5s_linear_forwards]"
          style={{ width: '100%' }}
        />
      )}
    </div>
  );
}
